<template>
  <div class="container-fluid px-4 mt-3">
    <!-- Fixed Position Alerts -->
    <transition name="fade">
      <div v-if="showSuccessMessage" class="alert alert-success alert-dismissible fade show" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 1050; max-width: 400px; min-width: 300px;">
        <div class="d-flex align-items-start">
          <i class="fas fa-check-circle me-2 mt-1"></i>
          <div class="flex-grow-1" v-html="successMessage"></div>
          <button type="button" @click="showSuccessMessage = false" aria-label="Close" style="background: none; border: 1px solid #c3e6cb; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; margin-left: 8px; cursor: pointer;">
            <span style="color: #155724; font-size: 14px; font-weight: bold;">&times;</span>
          </button>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="showErrorMessage" class="alert alert-danger alert-dismissible fade show" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 1050; max-width: 500px; min-width: 300px;">
        <div class="d-flex align-items-start">
          <i class="fas fa-exclamation-circle me-2 mt-1"></i>
          <div class="flex-grow-1">
            <pre style="white-space: pre-wrap; margin: 0; font-family: inherit;">{{ errorMessage }}</pre>
          </div>
          <button type="button" @click="showErrorMessage = false" aria-label="Close" style="background: none; border: 1px solid #f5c6cb; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; margin-left: 8px; cursor: pointer;">
            <span style="color: #721c24; font-size: 14px; font-weight: bold;">&times;</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2><i class="fas fa-route me-2"></i>Next Course Determination Settings</h2>
      <button 
        class="btn btn-outline-secondary"
        @click="refreshData"
        :disabled="loading"
      >
        <i v-if="!loading" class="fas fa-sync me-1"></i>
        <span v-else class="spinner-border spinner-border-sm me-1"></span>
        {{ loading ? 'Loading...' : 'Refresh' }}
      </button>
    </div>

    <!-- Add New Setting Form -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0"><i class="fas fa-plus me-2"></i>Add New Determination Setting</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <label for="newCourse" class="form-label">Course *</label>
            <select 
              class="form-select" 
              id="newCourse"
              v-model="newSetting.course_id"
              :class="{ 'is-invalid': validationErrors.course_id }"
            >
              <option value="">Select Course</option>
              <option 
                v-for="course in courses" 
                :key="course._id" 
                :value="course._id"
              >
                {{ course.name }}
              </option>
            </select>
            <div v-if="validationErrors.course_id" class="invalid-feedback">
              {{ validationErrors.course_id }}
            </div>
          </div>
          
          <div class="col-md-3">
            <label for="newDate" class="form-label">Determination Date *</label>
            <input 
              type="date" 
              class="form-control" 
              id="newDate"
              v-model="newSetting.determination_date"
              :class="{ 'is-invalid': validationErrors.determination_date }"
            />
            <div v-if="validationErrors.determination_date" class="invalid-feedback">
              {{ validationErrors.determination_date }}
            </div>
          </div>
          
          <div class="col-md-2">
            <label for="newAge" class="form-label">Required Age *</label>
            <input 
              type="number" 
              class="form-control" 
              id="newAge"
              v-model.number="newSetting.required_age"
              min="0" 
              max="150"
              :class="{ 'is-invalid': validationErrors.required_age }"
            />
            <div v-if="validationErrors.required_age" class="invalid-feedback">
              {{ validationErrors.required_age }}
            </div>
          </div>
          
          <div class="col-md-3">
            <label for="newNextCourse" class="form-label">Next Course Name *</label>
            <input 
              type="text" 
              class="form-control" 
              id="newNextCourse"
              v-model="newSetting.next_course_name"
              placeholder="Enter next course name"
              :class="{ 'is-invalid': validationErrors.next_course_name }"
            />
            <div v-if="validationErrors.next_course_name" class="invalid-feedback">
              {{ validationErrors.next_course_name }}
            </div>
          </div>
          
          <div class="col-md-1 d-flex align-items-end">
            <button 
              class="btn btn-primary w-100"
              @click="saveSetting"
              :disabled="saving"
            >
              <i v-if="!saving" class="fas fa-save"></i>
              <span v-else class="spinner-border spinner-border-sm"></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings List -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0"><i class="fas fa-list me-2"></i>Current Settings ({{ settings.length }})</h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        
        <div v-else-if="settings.length === 0" class="text-center py-4">
          <p class="text-muted">No determination settings found. Add one above to get started.</p>
          <p v-if="courses.length === 0" class="text-warning">
            <i class="fas fa-exclamation-triangle me-1"></i>
            No courses loaded. Check browser console for errors.
          </p>
        </div>
        
        <div v-else class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Course</th>
                <th>Determination Date</th>
                <th>Required Age</th>
                <th>Next Course Name</th>
                <th width="120">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="setting in settings" :key="setting._id">
                <td>
                  <div v-if="editingId === setting._id">
                    <select 
                      class="form-select form-select-sm" 
                      v-model="editingSetting.course_id"
                    >
                      <option 
                        v-for="course in courses" 
                        :key="course._id" 
                        :value="course._id"
                      >
                        {{ course.name }}
                      </option>
                    </select>
                  </div>
                  <div v-else>
                    {{ setting.course_id?.name || 'Unknown Course' }}
                  </div>
                </td>
                
                <td>
                  <div v-if="editingId === setting._id">
                    <input 
                      type="date" 
                      class="form-control form-control-sm" 
                      v-model="editingSetting.determination_date"
                    />
                  </div>
                  <div v-else>
                    {{ formatDate(setting.determination_date) }}
                  </div>
                </td>
                
                <td>
                  <div v-if="editingId === setting._id">
                    <input 
                      type="number" 
                      class="form-control form-control-sm" 
                      v-model.number="editingSetting.required_age"
                      min="0" 
                      max="150"
                    />
                  </div>
                  <div v-else>
                    {{ setting.required_age }} years
                  </div>
                </td>
                
                <td>
                  <div v-if="editingId === setting._id">
                    <input 
                      type="text" 
                      class="form-control form-control-sm" 
                      v-model="editingSetting.next_course_name"
                    />
                  </div>
                  <div v-else>
                    {{ setting.next_course_name }}
                  </div>
                </td>
                
                <td>
                  <div v-if="editingId === setting._id" class="btn-group btn-group-sm">
                    <button 
                      class="btn btn-success"
                      @click="saveEdit(setting._id)"
                      :disabled="saving"
                      title="Save changes"
                    >
                      <i v-if="saving" class="spinner-border spinner-border-sm"></i>
                      <i v-else class="fas fa-check"></i>
                    </button>
                    <button 
                      class="btn btn-secondary"
                      @click="cancelEdit"
                      :disabled="saving"
                      title="Cancel"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <div v-else class="btn-group btn-group-sm">
                    <button 
                      class="btn btn-outline-primary"
                      @click="startEdit(setting)"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      class="btn btn-outline-danger"
                      @click="confirmDelete(setting)"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this determination setting?</p>
            <div v-if="settingToDelete" class="bg-light p-3 rounded">
              <strong>Course:</strong> {{ settingToDelete.course_id?.name }}<br>
              <strong>Date:</strong> {{ formatDate(settingToDelete.determination_date) }}<br>
              <strong>Age:</strong> {{ settingToDelete.required_age }} years<br>
              <strong>Next Course:</strong> {{ settingToDelete.next_course_name }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button 
              type="button" 
              class="btn btn-danger"
              @click="deleteSetting"
              :disabled="deleting"
            >
              <i v-if="deleting" class="spinner-border spinner-border-sm me-1"></i>
              <i v-else class="fas fa-trash me-1"></i>
              {{ deleting ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  name: 'NextCourseDetermination',
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      settings: [],
      courses: [],
      
      // New setting form
      newSetting: {
        course_id: '',
        determination_date: '',
        required_age: null,
        next_course_name: ''
      },
      
      // Editing
      editingId: null,
      editingSetting: {},
      
      // Deletion
      settingToDelete: null,
      
      // Validation
      validationErrors: {},
      
      // Messages
      showSuccessMessage: false,
      showErrorMessage: false,
      successMessage: '',
      errorMessage: ''
    };
  },
  
  async created() {
    await this.loadData();
  },
  
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadSettings(),
          this.loadCourses()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
        this.showError('Failed to load data');
      } finally {
        this.loading = false;
      }
    },
    
    async loadSettings() {
      const authToken = localStorage.getItem("auth_token");
      if (!authToken) {
        this.$router.push('/signin');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/determination-settings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load settings');
      }
      
      const data = await response.json();
      this.settings = data.data || [];
    },
    
    async loadCourses() {
      const authToken = localStorage.getItem("auth_token");
      if (!authToken) {
        console.error('No auth token found');
        return;
      }
      
      console.log('Loading courses from:', `${API_BASE_URL}/api/courses`);
      
      const response = await fetch(`${API_BASE_URL}/api/courses`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      console.log('Courses response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Failed to load courses:', response.status, errorData);
        throw new Error(`Failed to load courses: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Courses data received:', data);
      
      // Handle different possible response formats
      if (Array.isArray(data)) {
        this.courses = data;
      } else if (data.data && Array.isArray(data.data)) {
        this.courses = data.data;
      } else if (data.courses && Array.isArray(data.courses)) {
        this.courses = data.courses;
      } else {
        console.warn('Unexpected courses data format:', data);
        this.courses = [];
      }
      
      console.log('Loaded courses:', this.courses.length);
    },
    
    validateNewSetting() {
      this.validationErrors = {};
      
      if (!this.newSetting.course_id) {
        this.validationErrors.course_id = 'Course is required';
      }
      
      if (!this.newSetting.determination_date) {
        this.validationErrors.determination_date = 'Determination date is required';
      }
      
      if (this.newSetting.required_age === null || this.newSetting.required_age === '') {
        this.validationErrors.required_age = 'Required age is required';
      } else if (this.newSetting.required_age < 0 || this.newSetting.required_age > 150) {
        this.validationErrors.required_age = 'Age must be between 0 and 150';
      }
      
      if (!this.newSetting.next_course_name?.trim()) {
        this.validationErrors.next_course_name = 'Next course name is required';
      }
      
      return Object.keys(this.validationErrors).length === 0;
    },
    
    async saveSetting() {
      if (!this.validateNewSetting()) {
        return;
      }
      
      this.saving = true;
      try {
        const authToken = localStorage.getItem("auth_token");
        const response = await fetch(`${API_BASE_URL}/api/courses/${this.newSetting.course_id}/determination-settings`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.newSetting)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to create setting');
        }
        
        this.showSuccess('Determination setting created successfully');
        this.resetNewSetting();
        await this.loadSettings();
        
      } catch (error) {
        console.error('Error saving setting:', error);
        this.showError(error.message);
      } finally {
        this.saving = false;
      }
    },
    
    resetNewSetting() {
      this.newSetting = {
        course_id: '',
        determination_date: '',
        required_age: null,
        next_course_name: ''
      };
      this.validationErrors = {};
    },
    
    startEdit(setting) {
      this.editingId = setting._id;
      this.editingSetting = {
        course_id: setting.course_id?._id || setting.course_id,
        determination_date: setting.determination_date.split('T')[0],
        required_age: setting.required_age,
        next_course_name: setting.next_course_name
      };
    },
    
    cancelEdit() {
      this.editingId = null;
      this.editingSetting = {};
    },
    
    async saveEdit(settingId) {
      this.saving = true;
      try {
        const authToken = localStorage.getItem("auth_token");
        const response = await fetch(`${API_BASE_URL}/api/determination-settings/${settingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.editingSetting)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to update setting');
        }
        
        this.showSuccess('Setting updated successfully');
        this.cancelEdit();
        await this.loadSettings();
        
      } catch (error) {
        console.error('Error updating setting:', error);
        this.showError(error.message);
      } finally {
        this.saving = false;
      }
    },
    
    confirmDelete(setting) {
      this.settingToDelete = setting;
      const modal = new Modal(document.getElementById('deleteModal'));
      modal.show();
    },
    
    async deleteSetting() {
      if (!this.settingToDelete) return;
      
      this.deleting = true;
      try {
        const authToken = localStorage.getItem("auth_token");
        const response = await fetch(`${API_BASE_URL}/api/determination-settings/${this.settingToDelete._id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to delete setting');
        }
        
        this.showSuccess('Setting deleted successfully');
        await this.loadSettings();
        
        // Hide modal
        const modal = Modal.getInstance(document.getElementById('deleteModal'));
        modal.hide();
        
      } catch (error) {
        console.error('Error deleting setting:', error);
        this.showError(error.message);
      } finally {
        this.deleting = false;
        this.settingToDelete = null;
      }
    },
    
    async refreshData() {
      await this.loadData();
      this.showSuccess('Data refreshed successfully');
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    showSuccess(message) {
      this.successMessage = message;
      this.showSuccessMessage = true;
      setTimeout(() => {
        this.showSuccessMessage = false;
      }, 5000);
    },
    
    showError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
      setTimeout(() => {
        this.showErrorMessage = false;
      }, 8000);
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}

.form-control.is-invalid,
.form-select.is-invalid {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.invalid-feedback {
  display: block;
}
</style>
